import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectStudentTicketDetails,
  selectStudentTicketsUpdate,
  tryCloseTicket,
  tryCreateStudentTicket,
  tryGetAttachmentByTicketDetail,
  tryGetStudentTicketDetails,
} from "../../../store/slices/student-tickets";
import { selectCurrentUser } from "../../../store/slices/currentUser";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Paper,
  Typography,
  Alert,
  DialogTitle,
  Box,
  Stack,
} from "@mui/material";
import {
  Close,
  Send,
  Attachment,
  CloudUploadOutlined,
  UploadFileOutlined,
  Lock,
  LockOpen,
} from "@mui/icons-material";
import getExtensionFromBase64 from "../../../services/functions/getExtensionFromBase64";
import { useDropzone } from "react-dropzone";
import StudentTicketsSkeleton from "../skeleton/StudentTicketsSkeleton";
import moment from "moment/moment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const StudentTicketsDrawer = ({
  openTicketDrawer,
  setOpenTicketDrawer,
  ticketData,
  clientId,
  businessId,
  setDialog,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentUser = useSelector(selectCurrentUser);
  const messageSent = useSelector(selectStudentTicketsUpdate);
  const data = useSelector(selectStudentTicketDetails);
  const topFocusRef = useRef(null);

  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [closed, setClosed] = useState(false);

  const handleCheckboxChange = (event) => {
    setClosed(event.target.checked);
    setConfirmationDialog(event.target.checked);
  };

  useEffect(() => {
    !confirmationDialog && setClosed(false);
  }, [confirmationDialog]);

  const scrollToBottom = () => {
    topFocusRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const { isLoading } = data;
  const { topicId, subTopicId, statusTicketId, closeDate, subject } = data.item;

  const [attachment, setAttachment] = useState(null);
  const [message, setMessage] = useState("");
  const [openAttachmentLoaderDialog, setOpenAttachmentLoaderDialog] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  const ticketClose = async (ticketId) => {
    const action = await dispatch(tryCloseTicket(ticketId));
    if (action.payload.data.isValid) setOpenTicketDrawer(false);
    setDialog(true);
  };

  const handleOpenAttachmentPreview = async (ticketId, ticketDetaildId) => {
    const action = await dispatch(
      tryGetAttachmentByTicketDetail({ businessId, ticketId, ticketDetaildId })
    );

    if (action.payload.isValid) {
      try {
        var attachmentElement = document.createElement("a");
        const fileExtension = getExtensionFromBase64(action.payload.data.file);
        attachmentElement.href = action.payload.data.file;

        attachmentElement.download = `Archivo-${moment().format(
          "DD-MM-YYYY-HHmmss"
        )}.${fileExtension}`;

        attachmentElement.click();
      } catch (error) { }
    }
  };

  const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
    noKeyboard: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
    multiple: false,
    maxSize: 10000000,
    onDropAccepted: (acceptedFiles) => {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64data = reader.result;
        setAttachment(base64data.toString());
      };
    },
  });

  const files = acceptedFiles.map((filename) => (
    <ListItem key={filename.path}>
      <ListItemIcon>
        <UploadFileOutlined color="primary" />
      </ListItemIcon>
      <ListItemText primary={filename.path} />
    </ListItem>
  ));

  const handleSendMessage = async () => {
    const request = {};
    request.ticketDetails = [];

    request.ticketDetails.push({
      ticketDetailId: 0,
      ticketId: ticketData.ticketId,
      senderId: currentUser.roleId !== 1 ? clientId : currentUser.clientId,
      message: message,
      attachment: attachment,
      statusMessageId: 1,
    });

    request.ticketId = ticketData.ticketId;
    request.ownerId = 0;
    request.originalSenderId = clientId;
    request.topicId = topicId;
    request.subTopicId = subTopicId;
    request.subject = subject.trim();
    request.statusTicketId = statusTicketId;
    request.closeDate = closeDate;

    await dispatch(tryCreateStudentTicket({ businessId, request }));

    setMessage("");
    setAttachment(null);
    fetchData();
  };

  const RenderTicketDetails = () => {
    setShowSkeleton(true);
    return !isLoading ? (
      <List disablePadding>
        {data.item.ticketDetails.map((tDetail, index) => {
          const isSender = tDetail.senderId === clientId;
          return (
            <ListItem
              key={index}
              sx={{
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: isSender ? "#e1f5fe" : "white",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
                alignItems: isSender ? "flex-end" : "flex-start",
                alignSelf: isSender ? "flex-end" : "flex-start",
                maxWidth: { xs: "90%", sm: "80%" },
                ml: isSender ? "auto" : 0,
                mr: isSender ? 0 : "auto",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography
                variant="body1"
                align={isSender ? "right" : "left"}
                color="text.primary" // <--- Agregamos esto para forzar que el texto sea oscuro
                sx={{ wordBreak: "break-word", width: "100%", mb: 0.5 }}
              >
                {tDetail.message}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: isSender ? "flex-end" : "flex-start", width: "100%" }}>
                {tDetail.attachment && (
                  <Typography
                    variant="caption"
                    onClick={() => handleOpenAttachmentPreview(data.item.ticketId, tDetail.ticketDetailId)}
                    color="primary"
                    sx={{
                      ":hover": { cursor: "pointer", textDecoration: "underline" },
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontWeight: "bold",
                      mb: 0.5
                    }}
                  >
                    <Attachment fontSize="small" /> Descargar adjunto
                  </Typography>
                )}
                <Typography fontSize={11} color="text.secondary">
                  {moment(tDetail.messageDate).format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Box>
            </ListItem>
          );
        })}
      </List>
    ) : (
      showSkeleton && <StudentTicketsSkeleton />
    );
  };

  const fetchData = async () => {
    const studentId = businessId;
    const ticketId = ticketData.ticketId;
    await dispatch(tryGetStudentTicketDetails({ studentId, ticketId }));
    scrollToBottom();
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={openTicketDrawer}
        PaperProps={{
          sx: {
            zIndex: 1203,
            width: { xs: "100vw", sm: 500, md: "40vw" },
            maxWidth: "100%",
            height: { xs: "90vh", sm: "100%" },
            borderRadius: { xs: "12px 12px 0 0", sm: 0 },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
        onClose={() => setOpenTicketDrawer(false)}
        sx={{
          zIndex: 1203,
          flexShrink: 0,
        }}
      >
        {/* Header Drawer */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#194B6F",
            color: "white",
            position: "relative",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 4 },
            pb: { xs: 1.5, sm: 3 },
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              color: "white",
            }}
            onClick={() => setOpenTicketDrawer(false)}
          >
            <Close />
          </IconButton>

          <Typography
            variant="h6"
            align="left"
            sx={{ pr: 4, fontWeight: 700, lineHeight: 1.2 }}
            autoFocus
            ref={topFocusRef}
          >
            {subject}
          </Typography>
          <Typography variant="body2" align="left" sx={{ mt: 1, opacity: 0.9 }}>
            Tópico: {ticketData.topic} | Sub Tópico: {ticketData.subTopic}
          </Typography>
        </Box>

        {/* Chat Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#f5f5f5",
            p: { xs: 1.5, sm: 3 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RenderTicketDetails />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "white",
            p: { xs: 1.5, sm: 2 },
            borderTop: "1px solid #e0e0e0",
          }}
        >
          {statusTicketId !== 3 ? (
            <>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="outlined-adornment-message">
                  Mensaje
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-message"
                  type="text"
                  multiline
                  maxRows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenAttachmentLoaderDialog(true)}
                        color={attachment ? "primary" : "default"}
                        sx={{ mr: 1 }}
                        aria-label="toggle attachment"
                        edge="end"
                      >
                        <Attachment />
                      </IconButton>
                      <IconButton
                        disabled={message.trim().length === 0 || messageSent.isLoading}
                        onClick={() => handleSendMessage()}
                        color="primary"
                        aria-label="send message"
                        edge="end"
                      >
                        <Send />
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Mensaje"
                />
              </FormControl>

              {currentUser.roleId == 1 && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                  {closed ? (
                    <Lock color="secondary" fontSize="small" sx={{ mr: 1 }} />
                  ) : (
                    <LockOpen color="secondary" fontSize="small" sx={{ mr: 1 }} />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={closed}
                        onChange={handleCheckboxChange}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" fontWeight="bold">CERRAR TICKET</Typography>}
                    color="secondary"
                  />
                  {closed && (
                    <Alert severity="warning" sx={{ ml: 2, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                      Esta acción no se puede deshacer.
                    </Alert>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info">
              {currentUser.roleId !== 1
                ? "Este ticket ha sido cerrado. Si todavía tienes alguna pregunta, no dudes en comunicarte con nosotros nuevamente."
                : "Este ticket ha sido cerrado."}
            </Alert>
          )}
        </Box>
      </Drawer>

      {/* Dialog Attachment */}
      <Dialog
        open={openAttachmentLoaderDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              border: "2px dashed #85BFE6",
              borderRadius: "12px",
              p: 2,
              mt: 1,
            }}
          >
            <section className="container">
              <div {...getRootProps({ className: "dropzone", style: { cursor: "pointer" } })}>
                <input {...getInputProps()} />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2} display="flex" justifyContent="center">
                    <CloudUploadOutlined color="primary" fontSize="large" />
                  </Grid>
                  <Grid item xs={12} sm={6} textAlign={{ xs: "center", sm: "left" }}>
                    <Typography color="primary" variant="subtitle2" fontWeight={700}>
                      Seleccione un archivo o arrástrelo hasta acá
                    </Typography>
                    <Typography color="primary" variant="caption">
                      JPG, PNG o PDF, tamaño no mayor a 10MB
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} display="flex" justifyContent="center">
                    <Button variant="outlined" color="secondary" size="small">
                      Buscar
                    </Button>
                  </Grid>
                </Grid>
              </div>
              {attachment && <List sx={{ width: "100%", mt: 2 }}>{files}</List>}
            </section>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAttachment(null)} color="error">
            Borrar
          </Button>
          <Button onClick={() => setOpenAttachmentLoaderDialog(false)} variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Confirmation */}
      <Dialog
        open={confirmationDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          ¿Confirmas cerrar el ticket?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => ticketClose(ticketData.ticketId)} autoFocus variant="contained">
            Aceptar
          </Button>
          <Button onClick={() => setConfirmationDialog(false)} color="error">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentTicketsDrawer;